import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { initializePayment } from '../api/payment-api';
import SelectFine from './SelectFine';

import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Typography,
  Box,
  Divider,
  InputAdornment,
  CircularProgress,
  DialogActions,
  DialogContentText,
  withStyles,
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import { useBalance } from '../store/BalanceContext';

const BalanceTopupDialog = ({ classes, open, onClose }) => {
  const [dialogError, setDialogError] = useState('');
  const [initSuccess, setInitSuccess] = useState(false);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCloseWarning, setShowCloseWarning] = useState(false);
  const { t } = useTranslation();

  const {
    paymentMethods,
    selectedVariant,
    isLoadingPaymentMethods,
    fetchError,
    currency,
    updateSelectedVariant,
  } = useBalance();

  const totalAmount = useMemo(() => {
    if (!amount || !selectedVariant) return '';

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return '';

    const percentFee = numAmount * (parseFloat(selectedVariant.percent) / 100);
    const fixedFee = parseFloat(selectedVariant.fixed);
    const total = numAmount + percentFee + fixedFee;

    return total.toFixed(2);
  }, [amount, selectedVariant]);

  function handleAmountChange(event) {
    setAmount(event.target.value);
    if (dialogError === t('error_amount')) {
      setDialogError('');
    }
  }

  function handleVariantChange(value) {
    updateSelectedVariant(value);
  }

  const handleClose = () => {
    if (initSuccess) {
      setShowCloseWarning(true);
    } else {
      onClose();
    }
  };

  const handleCloseConfirm = () => {
    setShowCloseWarning(false);
    setInitSuccess(false);
    setDialogError('');
    setLoading(false);
    setAmount('');
    onClose();
  };

  const handleCloseCancel = () => {
    setShowCloseWarning(false);
  };

  const handleTopup = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setDialogError(t('error_amount'));
      return;
    }

    setLoading(true);
    setDialogError('');

    try {
      const payload = {
        amount: parseFloat(amount),
        currency: currency,
        return_url: `${window.location.origin}/payment-result`,
      };
      const response = await initializePayment(payload);

      if (!response || !response.data || !response.data.url) {
        throw new Error(t('error_something'));
      }

      const script = document.body.appendChild(
        document.createElement('script')
      );
      script.src = response.data.url;
      script.integrity = response.data.integrity;
      script.crossOrigin = 'anonymous';
      script.onload = () => {
        setInitSuccess(true);
      };
    } catch (e) {
      setDialogError(e.message);
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        maxWidth="xs"
        fullWidth
        onClose={handleClose}
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle className={classes.dialogTitle}>
          <Typography variant="h6">{t('topup')}</Typography>
        </DialogTitle>
        <Divider />
        <DialogContent className={classes.dialogContent}>
          {initSuccess ? (
            <Box p={2} height={320}>
              <form
                action={`${window.location.origin}/payment-result`}
                className="paymentWidgets"
                data-brands="VISA MASTER AMEX"
              ></form>
            </Box>
          ) : isLoadingPaymentMethods ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress size={40} color="primary" />
            </Box>
          ) : fetchError ? (
            <Box
              p={2}
              height={320}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Typography color="error">{fetchError}</Typography>
            </Box>
          ) : (
            <form>
              <Box mb={3}>
                <TextField
                  name="amount"
                  type="number"
                  label={t('amount')}
                  variant="outlined"
                  fullWidth
                  value={amount}
                  onChange={handleAmountChange}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">{currency}</InputAdornment>
                    ),
                    classes: { root: classes.inputField },
                  }}
                  error={!!dialogError}
                  helperText={dialogError}
                />
              </Box>

              {paymentMethods && paymentMethods.length > 1 && (
                <Box mb={3}>
                  <SelectFine
                    items={paymentMethods}
                    value={selectedVariant?.value || ''}
                    onChange={handleVariantChange}
                    label={t('payment_provider')}
                    classes={{ lang: classes.selectField }}
                    variant="outlined"
                    fullWidth
                    white
                  />
                </Box>
              )}

              {selectedVariant && amount && (
                <Box mb={3} className={classes.feeInfo}>
                  <Typography variant="body2" color="textSecondary">
                    {t('charge', {
                      percent: selectedVariant.percent,
                      fixed: selectedVariant.fixed,
                      currency,
                    })}
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    className={classes.totalAmount}
                  >
                    {t('total_amount')}:{' '}
                    <strong>
                      {totalAmount} {currency}
                    </strong>
                  </Typography>
                </Box>
              )}

              <Button
                className={classes.actionButton}
                color="primary"
                variant="contained"
                onClick={handleTopup}
                fullWidth
                disabled={
                  loading || !selectedVariant || !amount || !!fetchError
                }
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t('proceed')
                )}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={showCloseWarning}
        maxWidth="xs"
        fullWidth
        classes={{ paper: classes.warningDialog }}
      >
        <DialogTitle className={classes.warningTitle}>
          <Box display="flex" alignItems="center">
            <WarningIcon className={classes.warningIcon} />
            <Typography variant="h6">{t('payment_in_progress')}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{t('payment_close_warning')}</DialogContentText>
        </DialogContent>
        <DialogActions className={classes.warningActions}>
          <Button
            onClick={handleCloseCancel}
            color="primary"
            className={classes.cancelButton}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleCloseConfirm}
            color="primary"
            variant="contained"
            className={classes.confirmButton}
          >
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

BalanceTopupDialog.defaultProps = {
  open: true,
};

const styles = (theme) => {
  return {
    dialogPaper: {
      borderRadius: '12px',
      overflow: 'hidden',
    },
    selectField: {
      backgroundColor: '#f5f5f5',
      minWidth: '265px',
      height: '32px',
      borderRadius: '10px',
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },
    dialogTitle: {
      backgroundColor: '#FFF',
      padding: theme.spacing(2),
    },
    dialogContent: {
      backgroundColor: '#FFF',
      padding: theme.spacing(3),
      overflowX: 'hidden',
    },
    inputField: {
      borderRadius: '10px',
    },
    actionButton: {
      ...theme.button.filled,
      width: '100% !important',
      height: '44px',
      borderRadius: '10px',
    },
    feeInfo: {
      width: '100%',
      padding: theme.spacing(2),
      backgroundColor: '#f5f5f5',
      borderRadius: '10px',
    },
    totalAmount: {
      marginTop: theme.spacing(1),
      fontWeight: 500,
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '200px',
    },
    warningDialog: {
      borderRadius: '12px',
    },
    warningTitle: {
      backgroundColor: '#fff3e0',
    },
    warningIcon: {
      color: '#ff9800',
      marginRight: theme.spacing(1),
      fontSize: 28,
    },
    warningActions: {
      padding: theme.spacing(2),
    },
    cancelButton: {
      ...theme.button.transparent,
      marginRight: theme.spacing(1),
    },
    confirmButton: {
      ...theme.button.filled,
    },
  };
};

export default withStyles(styles)(BalanceTopupDialog);
